/**
 * 「提交前要素预填」的**提前发车**装置。
 *
 * ════════ 为什么要提前发车 ════════
 *
 * 实测后端中位 1.62 秒、p90 2.13 秒。若等市民按下「提交投诉」才开始算，
 * 他就要对着一个转圈的按钮等两秒；而他写完「投诉内容及诉求」之后**还要填**姓名、电话、
 * 被投诉方、传证据 —— 那段时间少说十几秒，白白空着。
 *
 * 所以发车点定在**「投诉内容及诉求」文本框失焦那一刻**（`onBlur`）：手指一离开，
 * 原文就上路了；等他把剩下几栏填完再点提交，结果早就在那儿了，**感知等待≈0**。
 *
 * ════════ 两条必须守住的 ════════
 *
 * ① **绝不挡住提交。** 本 hook 不导出任何能让提交按钮变灰的东西。
 *    超时、跑飞、后端 404、断网 —— 一律落到「五项留空且可填」，提交照走。
 *    这不是 mock 回落（市民端红线）：**留空是如实的「没拿到」**，不是编一份数据糊上去。
 *
 * ② **原文没变就不重算。** 焦点在文本框上来回进出是很常见的（点一下证据、
 *    再回来看一眼），不判一道就会来回打接口。`lastSent` 记住上一次**真正送出去**的
 *    那段原文，一字不差就直接返回，连一个 fetch 都不发。
 *
 * ════════ 并发 ════════
 *
 * 他可能改完原文再改一次（原文变了两次），于是两趟请求同时在飞。`seq` 递增，
 * 只有**最后发出去的那一趟**的结果作数 —— 否则先发后到的旧结果会盖掉新结果，
 * 表现是「我明明改了投诉内容及诉求，识别出来的还是上一版」。
 */

import { useCallback, useRef, useState } from 'react';
import { postPrefill } from '../api/prefill';
import { PREFILL_LIMITS } from '@cdfjx/shared/dist/prefill-contract';
import type { PrefillResult, PrefillStatus } from '@cdfjx/shared/dist/prefill-contract';

/**
 * 界面要知道的三种态。
 *
 *   idle     还没发过车（或上一趟已经落地）
 *   running  正在算 —— 界面上必须有「正在识别」的样子。
 *            正常路径下市民根本看不到它（1.6 秒里他还在填姓名），
 *            但他填得飞快时会看到，那一刻界面不能是一片空白。
 *   done     这一趟落地了（成没成看 `status`）
 */
export type PrefillPhase = 'idle' | 'running' | 'done';

export interface PrefillHandle {
  phase: PrefillPhase;
  /** 最近一趟怎么收场的；从没跑过为 `null`。界面据此区分「没跑成」与「跑了但判不出」 */
  status: PrefillStatus | null;
  /**
   * 发车。传当前原文即可，去重与并发都在里面处理。
   * **同步返回，不 await** —— 调用方（失焦回调、切输入通道、按下一步）一律不等它。
   */
  start: (text: string) => void;
  /**
   * 重新识别一次（无视「原文没变就不重算」那道判断）。
   * 只挂在**没跑成功**时那句提示旁边的「重新识别」上 —— 是市民自己按的，不是自动重试。
   */
  /**
   * 实际发出去过几趟网络请求。**只给验收用**（证明「原文没变不重算」），
   * 界面上不渲染它 —— 页面上不写解释系统怎么运转的话。
   */
  calls: number;
}

export function usePrefill(onResult: (r: PrefillResult) => void): PrefillHandle {
  const [phase, setPhase] = useState<PrefillPhase>('idle');
  const [status, setStatus] = useState<PrefillStatus | null>(null);
  const [calls, setCalls] = useState(0);
  /** 上一次**真正送出去**算过的原文。没变就不重算。 */
  const lastSent = useRef('');
  /** 递增序号：只认最后一趟的结果 */
  const seq = useRef(0);
  /** 回调放 ref 里，免得调用方每次渲染都换一个函数、把 `start` 的引用也带得一起变 */
  const cb = useRef(onResult);
  cb.current = onResult;

  const fire = useCallback((raw: string, force: boolean) => {
    const text = raw.trim();
    // 太短的不送：契约真本定的下限，与提交那道校验同一口径。
    // 不记进 lastSent —— 他接着往下写，文本一变就该重新判一次。
    if (text.length < PREFILL_LIMITS.textMin) {
      setStatus('skipped');
      setPhase('done');
      return;
    }
    // ★ 原文一字不差＝这一趟不必跑。连 fetch 都不发。
    //   这不只是省一次等待：prefill 每调一次都真花钱打模型，而这条链路目前无限流。
    if (!force && text === lastSent.current) return;

    lastSent.current = text;
    const mine = ++seq.current;
    setPhase('running');
    setCalls((n) => n + 1);

    void postPrefill(text)
      .then((r) => {
        if (mine !== seq.current) return; // 旧的一趟，丢掉
        setStatus(r.status);
        setPhase('done');
        cb.current(r);
      })
      .catch(() => {
        if (mine !== seq.current) return;
        /* ★ 连不上 / 404 / 服务端异常。**这里不编任何数据**：
             不调 cb，五项保持原样（多半就是空的），界面照常可填、提交照常可按。
             之所以标 'failed' 而不是 'ok'，是因为「没跑成」与「跑了但判不出」
             在界面上要说两句不同的话 —— 混成一句就是把故障说成了「本来就没内容」。 */
        setStatus('failed');
        setPhase('done');
        /* ★ 失败之后**不自动重试，也不给手动重试**，`lastSent` 照留。
             清掉它的话，焦点每进出文本框一次就重打一次接口 —— 后端本来就不好，
             再被前端连打只会更糟。
             ★ 2026-08-27 主公令：连「重新识别」按钮也去掉了。没跑出来时，
               市民直接自己填这几项比等一次重算快得多；摆个重试钮反而引他去等、
               去反复按，而每按一次都真花一次钱。 */
      });
  }, []);

  const start = useCallback((text: string) => fire(text, false), [fire]);

  return { phase, status, start, calls };
}
